import React from 'react';
import { LinearProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { CHAT_SCOPE } from 'src/contexts/assistant/constants';
import { AssistantBubble } from 'src/modules/assistant/components/AssistantBubble';
import { AssistantMessage } from 'src/modules/assistant/components/AssistantMessage';
import { AssistantTypingDots } from 'src/modules/assistant/components/AssistantTypingDots';
import { ASSISTANT_NAME } from 'src/modules/assistant/constants/widget';
import { TMessageActionsProps } from 'src/modules/assistant/types/actionDraft';
import { TChatScope } from 'src/contexts/assistant/types';
import { TAssistantMessage } from 'src/modules/assistant/types/message';

interface AssistantMessageListProps {
    scope: TChatScope;
    messages: TAssistantMessage[];
    canUseAssistant: boolean;
    contextLoading: boolean;
    leadRequiresAgent: boolean;
    busy: boolean;
    loading: boolean;
    error: string;
    actions: TMessageActionsProps;
    formatDateText: (value?: string) => string | undefined;
}

export const AssistantMessageList = ({
    scope,
    messages,
    canUseAssistant,
    contextLoading,
    leadRequiresAgent,
    busy,
    loading,
    error,
    actions,
    formatDateText,
}: AssistantMessageListProps) => {
    const { t } = useTranslation();

    return (
        <>
            <AssistantBubble>
                {t('Assistant.Introduction', { name: ASSISTANT_NAME })}{' '}
                {scope === CHAT_SCOPE.LEAD
                    ? t('Assistant.LeadIntroduction')
                    : t('Assistant.GeneralIntroduction')}
            </AssistantBubble>
            {!canUseAssistant && (
                <Alert role="status" severity="info">
                    {t(
                        contextLoading
                            ? 'Assistant.Loading'
                            : 'Assistant.AgencyDisabled'
                    )}
                </Alert>
            )}
            {canUseAssistant && leadRequiresAgent && (
                <Alert role="status" severity="warning">
                    <strong>{t('Assistant.AgentRequiredTitle')}</strong>
                    <br />
                    {t('Assistant.AgentRequiredMessage')}
                </Alert>
            )}
            {canUseAssistant &&
                messages.map((message) => (
                    <AssistantMessage
                        actions={actions}
                        formatDateText={formatDateText}
                        key={message._id}
                        message={message}
                    />
                ))}
            {busy && (
                <AssistantBubble
                    ariaLabel={t('Assistant.Analyzing')}
                    role="status"
                >
                    <AssistantTypingDots />
                </AssistantBubble>
            )}
            {loading && <LinearProgress aria-label={t('Assistant.Loading')} />}
            {error && <Alert severity="error">{error}</Alert>}
        </>
    );
};
